import type { ApiDocument, GetDocumentsResponse } from "@workith/shared/index";
import { Badge } from "@workith/ui";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DocumentGrid } from "../components/document/DocumentGrid";
import { DocumentGridSkeleton } from "../components/document/DocumentGridSkeleton";
import { DocumentList } from "../components/document/DocumentList";
import { DocumentViewModeToggle } from "../components/document/DocumentViewModeToggle";
import { useDocumentViewMode } from "../components/document/useDocumentViewMode";
import api from "../lib/api";

export function SharedPage() {
  const [documents, setDocuments] = useState<ApiDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useDocumentViewMode();
  const navigate = useNavigate();

  const openDocument = useCallback(
    (documentItem: ApiDocument) => {
      navigate(`/editor/${documentItem.id}`);
    },
    [navigate]
  );

  useEffect(() => {
    api
      .get<GetDocumentsResponse>("/api/documents/shared")
      .then((data) => setDocuments(data.documents))
      .catch((err) => console.error("Failed to fetch shared documents:", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-6">
        <DocumentGridSkeleton />
      </div>
    );
  }

  const DocumentView = viewMode === "list" ? DocumentList : DocumentGrid;

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold">Shared with me</h2>
        <DocumentViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
      </div>
      {documents.length === 0 ? (
        <p className="text-muted-foreground">No documents shared with you yet.</p>
      ) : (
        <DocumentView
          documents={documents}
          onOpenDocument={openDocument}
          renderBadge={(documentItem) => <Badge>{documentItem.user_role}</Badge>}
          renderDescription={(documentItem) => `Shared by ${documentItem.owner_name ?? "Unknown"}`}
        />
      )}
    </div>
  );
}
